import type React from 'react'
import { useEffect, useState } from 'react'
import { differenceInSeconds, isBefore } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { getShiftTimezone } from '../../../lib/shift'
import { Work } from '../../../typings/common_defs'
import { ShiftPayExplanationTag } from './ShiftPayExplanationTag'
import { useShiftDetail } from '../useShiftDetail'
import { Box, Flex, Label, Text, Tooltip } from 'ui'
import { BadgeCheckIcon, HelpCircleIcon } from 'lucide-react'
import { formatCurrency, toFirstName } from 'lib/string'
import { useAppTheme } from '@workwhile/ui'
import { BreakInfo } from './mealbreaks/BreakInfo'
import { isBillRateShift } from 'lib/payment'
import { useAuthContext } from 'components/auth'

interface Props {
  work: Work
  isNoShow: boolean
}

export const PastWorkInfo = ({ work, isNoShow }: Props): React.ReactElement => {
  const { company } = useAuthContext()
  const { shift, isOtherCompany } = useShiftDetail()
  const { colors } = useAppTheme()
  const [start, setStart] = useState<string>('')
  const [end, setEnd] = useState<string>('')
  const [hoursWorked, setHoursWorked] = useState<number>(0)
  const [totalPay, setTotalPay] = useState<number>(0)
  const [morePayThanActuallyWorked, setMorePayThanActuallyWorked] =
    useState<boolean>(false)
  const [workEmployerApprovedAt, setWorkEmployerApprovedAt] = useState<
    string | null
  >(null)

  const isBillRate = isBillRateShift(shift)

  useEffect(() => {
    if (!shift) return

    if (!work || !work.startedAt || !work.completedAt) return

    const fmt = 'h:mma z'
    const shifttz = getShiftTimezone(shift)

    const startedAtDate = new Date(work.startedAt)
    const completedAtDate = new Date(work.completedAt)
    const shiftEndsAtDate = new Date(shift.endsAt)
    const employerApprovedAtDate = work?.employerApprovedAt
      ? new Date(work.employerApprovedAt)
      : null

    setStart(formatInTimeZone(startedAtDate, shifttz, fmt))
    setEnd(formatInTimeZone(completedAtDate, shifttz, fmt))
    setWorkEmployerApprovedAt(
      employerApprovedAtDate
        ? formatInTimeZone(
            employerApprovedAtDate,
            shifttz,
            'EEE, MMM d h:mma zzz'
          )
        : null
    )

    const hrsWorked = work.timeWorkedSeconds
      ? Math.max(work.timeWorkedSeconds, 0) / 3600
      : 0
    setHoursWorked(hrsWorked)

    const ttlPay = work.earnings ? Math.max(work.earnings, 0) : 0
    setTotalPay(ttlPay)

    const roundedMinutesWorked = Math.round(
      differenceInSeconds(completedAtDate, startedAtDate) / 60
    )
    const lunchLength = work.lunchLengthOverride ?? shift.lunchLength
    const actualHoursWorked = (roundedMinutesWorked - (lunchLength || 0)) / 60

    const completedBeforeEndTime = isBefore(completedAtDate, shiftEndsAtDate)
    const receivingMorePayThanActuallyWorked =
      !work.leftEarly &&
      completedBeforeEndTime &&
      actualHoursWorked < hoursWorked

    setMorePayThanActuallyWorked(receivingMorePayThanActuallyWorked)
  }, [work, shift])

  const workerFirstName = work?.worker?.name
    ? toFirstName(work.worker.name)
    : 'This worker'
  // The company info is the active company's, so don't name it on another company's shift
  const companyName = isOtherCompany ? undefined : company?.name
  const companyTeam = companyName
    ? `the ${companyName}`
    : isOtherCompany
    ? 'the'
    : 'your'
  const approverName = work.employerApprovedBy?.name || null
  const approvalTooltip = approverName
    ? `${workerFirstName}'s work was approved by ${approverName} on ${workEmployerApprovedAt}`
    : `${workerFirstName}'s work was approved by ${companyTeam} Team on ${workEmployerApprovedAt}`
  const noShowTooltip = `${workerFirstName} was marked a No Show by ${companyTeam} Team and will be blocked from working at ${
    companyName ?? 'this company'
  } in the future`

  return (
    <Flex
      flexDirection={['column', 'column', 'row']}
      alignItems={['flex-start', 'flex-start', 'center']}
      justifyContent={['center', 'center', 'flex-start']}
      flexWrap="wrap"
      rowGap={1}
      minHeight="5rem"
      lineHeight="normal"
    >
      {isNoShow ? null : (
        <Flex flexDirection="row" flexWrap="wrap" mr={3}>
          {`${start} ${start && end ? '-' : ''} ${end} ${
            start || end ? '•' : ''
          } ${hoursWorked.toFixed(2)} hrs`}
          <>
            <BreakInfo
              workId={work.id}
              renderContent={({ description, detailTrigger }) => (
                <Flex alignItems={'center'}>
                  &nbsp;•&nbsp;{description} break&nbsp;{detailTrigger}&nbsp;
                </Flex>
              )}
            />{' '}
            {isBillRate ? null : (
              <>
                • Est. {formatCurrency(totalPay)}
                {'  '}
              </>
            )}
          </>
        </Flex>
      )}
      {morePayThanActuallyWorked && !isBillRate && !isNoShow ? (
        <Box mr={3}>
          <ShiftPayExplanationTag user={work?.worker} />
        </Box>
      ) : null}
      {work.status === 'bailed' ? (
        <Flex flexDirection="row" alignItems="center" mr={3}>
          <Text color="errors.200" mr={1}>
            No Show - Will Be Blocked
          </Text>
          <Box flex={1}>
            <Tooltip content="If this worker actually did show up, please contact WorkWhile Support">
              <HelpCircleIcon size={14} color={colors.primary} />
            </Tooltip>
          </Box>
        </Flex>
      ) : null}
      {work.supervisorMarkedNoShow ? (
        <Box flexShrink={0}>
          <Tooltip content={noShowTooltip}>
            <Label
              variant="none"
              size="small"
              mr={3}
              style={{ whiteSpace: 'nowrap' }}
            >
              Supervisor Marked No Show
            </Label>
          </Tooltip>
        </Box>
      ) : null}
      {workEmployerApprovedAt ? (
        <Flex flexDirection="row" alignItems="center" mr={3}>
          <Tooltip content={approvalTooltip}>
            <BadgeCheckIcon size={14} color={colors.success} />
          </Tooltip>
          {approverName ? (
            <Text ml={1} fontSize={1}>
              {`Approved by ${approverName} · ${workEmployerApprovedAt}`}
            </Text>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  )
}
